import { any, compose, identity } from 'underscore';
import React, { useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useActuatorsTracking } from './use-actuators-tracking';

const activate = ({ phase, actuators }) => {
  actuators.forEach(actuator => actuator.activate(phase));
};

export const withActuation = (WrappedComponent, { Actuators }) => {
  const Actuator = compose(...(Actuators || [identity]))(WrappedComponent);

  const HOC = ({ actuationProps, ...restProps }) => {
    const { needsActivation: needsActivationFromProps } = actuationProps;
    const needsActivationStateRef = useRef(needsActivationFromProps);
    needsActivationStateRef.current = needsActivationFromProps;

    const needsActivation = useCallback(({ phase, actuators }) => needsActivationStateRef.current && any(actuators, b => b.needsActivation(phase)), []);
    const {
      actuatorId, debugMode, registerActuator, unregisterActuator,
    } = useActuatorsTracking({ activate, needsActivation });

    const actuatorProps = useMemo(() => ({
      registerActuator,
      unregisterActuator,
      debugMode,
    }), [debugMode, registerActuator, unregisterActuator]);

    return (
      <>
        {debugMode &&
          <div>{`Actuator ${actuatorId}: ${needsActivationFromProps ? '!!!' : 'OK'}`}</div>
        }
        <Actuator
          {...restProps}
          {...Actuator.isActuator ? { actuatorProps, actuationProps } : {}}
        />
      </>
    );
  };

  HOC.propTypes = {
    actuationProps: PropTypes.shape({
      needsActivation: PropTypes.bool,
    }),
  };

  HOC.defaultProps = {
    actuationProps: {
      needsActivation: false,
    },
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `withActuation(${getDisplayName(WrappedComponent)})`;
  HOC.isActuator = true;

  return HOC;
};
